"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  Loader2,
  MessageSquareReply,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

import {
  useGetAllTicketsQuery,
  useDeleteTicketMutation,
} from "@/features/api/supportApi";

/* ================= STATUS BADGE ================= */
const getStatusBadge = (status = "pending") => {
  switch (String(status).toLowerCase()) {
    case "pending":
      return <Badge className="bg-yellow-500 text-white">Pending</Badge>;
    case "processing":
      return <Badge className="bg-blue-500 text-white">Processing</Badge>;
    case "replied":
      return <Badge className="bg-green-600 text-white">Replied</Badge>;
    case "solved":
      return <Badge className="bg-green-700 text-white">Solved</Badge>;
    default:
      return <Badge className="bg-gray-400 text-white">N/A</Badge>;
  }
};

export default function SupportList() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetAllTicketsQuery();

  // ✅ HARD GUARANTEE ARRAY
  const tickets = useMemo(
    () => (Array.isArray(data) ? data : []),
    [data]
  );

  const [deleteTicket, { isLoading: deleting }] =
    useDeleteTicketMutation();

  /* ================= FILTER ================= */
  const filteredTickets = useMemo(() => {
    if (!Array.isArray(tickets)) return [];

    const q = searchQuery.toLowerCase().trim();
    if (!q) return tickets;

    return tickets.filter((t) =>
      [t?.name, t?.email, t?.subject]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [tickets, searchQuery]);

  /* ================= SORT ================= */
  const sortedTickets = useMemo(() => {
    if (!Array.isArray(filteredTickets)) return [];

    return [...filteredTickets].sort((a, b) => {
      const aVal = a?.[sortBy] ?? "";
      const bVal = b?.[sortBy] ?? "";

      if (sortBy === "createdAt") {
        return sortOrder === "asc"
          ? new Date(aVal || 0) - new Date(bVal || 0)
          : new Date(bVal || 0) - new Date(aVal || 0);
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredTickets, sortBy, sortOrder]);

  const toggleSort = useCallback(
    (field) => {
      setSortOrder((p) =>
        p === "asc" && sortBy === field ? "desc" : "asc"
      );
      setSortBy(field);
    },
    [sortBy]
  );

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!id) return;

    if (!window.confirm("Delete this ticket?")) return;

    try {
      await deleteTicket(id).unwrap();
      toast.success("Ticket deleted");
    } catch (e) {
      toast.error(e?.data?.message || "Delete failed");
    }
  };

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || "Failed to load tickets");
    }
  }, [isError, error]);

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center mt-20">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!sortedTickets.length) {
    return (
      <div className="p-6 text-center text-gray-500">
        No support tickets found
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">
        🎫 Support Tickets
      </h2>

      <Input
        placeholder="Search..."
        className="mb-4 max-w-md"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <Table>
        <TableCaption>All support tickets</TableCaption>

        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            {["name", "email", "subject", "status", "createdAt"].map(
              (h) => (
                <TableHead
                  key={h}
                  onClick={() => toggleSort(h)}
                  className="cursor-pointer select-none"
                >
                  {h.toUpperCase()}
                </TableHead>
              )
            )}
            <TableHead className="text-right">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {sortedTickets.map((t, i) => (
            <TableRow key={t?._id ?? i}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{t?.name || "N/A"}</TableCell>
              <TableCell>{t?.email || "N/A"}</TableCell>
              <TableCell>{t?.subject || "N/A"}</TableCell>
              <TableCell>{getStatusBadge(t?.status)}</TableCell>
              <TableCell>
                {t?.createdAt
                  ? new Date(t.createdAt).toLocaleDateString()
                  : "N/A"}
              </TableCell>

              <TableCell className="flex justify-end gap-2">
                <Button
                  size="icon"
                  variant="outline"
                  disabled={!t?._id}
                  onClick={() =>
                    t?._id &&
                    navigate(`/admin/support/reply/${t._id}`)
                  }
                >
                  <MessageSquareReply size={16} />
                </Button>

                <Button
                  size="icon"
                  variant="outline"
                  disabled={!t?._id}
                  onClick={() =>
                    t?._id &&
                    navigate(`/admin/support/edit/${t._id}`)
                  }
                >
                  <Edit size={16} />
                </Button>

                <Button
                  size="icon"
                  variant="destructive"
                  disabled={deleting || !t?._id}
                  onClick={() => handleDelete(t._id)}
                >
                  <Trash2 size={16} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
